/**
 * Weekly-hours editor for a single service (pickup or delivery) on a
 * business location. Emits the backend HoursSpec shape:
 *   { weekly: { mon: [["09:00","18:00"]], ... } }  — or null when empty.
 * Owner-defined single window semantics (spec 2026-07-22 §2.1).
 *
 * Thin renderer over the pure helpers in ./weeklyHours — no interval
 * arithmetic here. The one bit of "ordering" logic that does live in this
 * component is sorting a day's intervals by open time after any time edit
 * (see handleSetTime): validateDay (and the server's HoursSpec validator)
 * are a single forward pass with no sort, so the UI must keep each day's
 * intervals pre-sorted by open time or it will show/raise spurious overlap
 * errors for a day whose intervals just happen to be entered out of order.
 */

import { useState } from 'react'
import type { WeeklyHours } from '@/types/commerce'
import { Button, Switch } from '@/components/ui'
import { cn } from '@/utils'
import {
  DAY_KEYS,
  DAY_LABELS,
  PRESETS,
  setDayIntervals,
  copyDayTo,
  applyToDays,
  closeDays,
  validateDay,
  summarize,
  normalize,
} from './weeklyHours'

const DEFAULT_INTERVAL: [string, string] = ['09:00', '18:00']

const FIELD_CLASSES =
  'h-7 rounded-md border border-input bg-transparent px-2 text-sm shadow-xs ' +
  'outline-none transition-[color,box-shadow] ' +
  'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]'

const FULL_DAY_NAMES: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
}

const COPY_TARGETS: Record<string, readonly string[]> = {
  all: DAY_KEYS,
  weekdays: DAY_KEYS.slice(0, 5),
  weekend: DAY_KEYS.slice(5),
}

interface Props {
  value: WeeklyHours | null
  onChange: (v: WeeklyHours | null) => void
  label: string
  copyFrom?: { label: string; value: WeeklyHours | null }
}

function sortByOpen(intervals: [string, string][]): [string, string][] {
  // Zero-padded 24h HH:MM strings compare safely as strings (see
  // weeklyHours.ts). This is reordering, not interval arithmetic.
  return [...intervals].sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
}

export function WeeklyHoursEditor({ value, onChange, label, copyFrom }: Props) {
  const weekly = value?.weekly ?? {}

  // Per-instance state only — a module-level variable here would leak the
  // seed/bulk fields between the pickup and delivery editors rendered
  // together on the same location form.
  const [lastInterval, setLastInterval] = useState<[string, string]>(DEFAULT_INTERVAL)
  const [bulkMode, setBulkMode] = useState(false)
  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set())
  const [bulkOpen, setBulkOpen] = useState(DEFAULT_INTERVAL[0])
  const [bulkClose, setBulkClose] = useState(DEFAULT_INTERVAL[1])
  // Pending copy-to target per day, keyed by day. The select only stages a
  // choice — the actual mutation requires an explicit "Copy" button click,
  // so arrow-keying through the collapsed <select> to reach an option can't
  // silently overwrite days on the way past (see handleCopy).
  const [copyTarget, setCopyTarget] = useState<Record<string, string>>({})

  const dayIntervals = (day: string): [string, string][] => weekly[day] ?? []

  const handleToggleDay = (day: string, open: boolean) => {
    onChange(setDayIntervals(value, day, open ? [lastInterval] : []))
  }

  const handleRemoveInterval = (day: string, idx: number) => {
    onChange(setDayIntervals(value, day, dayIntervals(day).filter((_, i) => i !== idx)))
  }

  const handleSetTime = (day: string, idx: number, pos: 0 | 1, time: string) => {
    const current = dayIntervals(day)
    const updated = current.map((iv, i) =>
      i === idx ? ((pos === 0 ? [time, iv[1]] : [iv[0], time]) as [string, string]) : iv,
    )
    setLastInterval(updated[idx])
    onChange(setDayIntervals(value, day, sortByOpen(updated)))
  }

  const handleCopy = (day: string) => {
    const targetKey = copyTarget[day]
    if (!targetKey) return
    onChange(copyDayTo(value, day, COPY_TARGETS[targetKey]))
    setCopyTarget((prev) => ({ ...prev, [day]: '' }))
  }

  const toggleSelected = (day: string, checked: boolean) => {
    setSelectedDays((prev) => {
      const next = new Set(prev)
      if (checked) next.add(day)
      else next.delete(day)
      return next
    })
  }

  const toggleBulkMode = () => {
    setBulkMode((prev) => {
      if (prev) setSelectedDays(new Set())
      return !prev
    })
  }

  const handleBulkApply = () => {
    onChange(applyToDays(value, Array.from(selectedDays), [bulkOpen, bulkClose]))
  }

  const handleBulkClose = () => {
    onChange(closeDays(value, Array.from(selectedDays)))
  }

  const handleCopyFrom = () => {
    if (!copyFrom?.value) return
    // Deep-copy so the two editors' state never alias the same object —
    // normalize() already returns a fresh copy of every interval array.
    onChange(normalize(copyFrom.value))
  }

  return (
    <div className="space-y-2.5">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>

      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((preset) => (
          <Button
            key={preset.id}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onChange(preset.build())}
            className="h-6 px-2 text-xs font-normal text-primary"
          >
            {preset.label}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant={bulkMode ? 'secondary' : 'outline'}
          aria-pressed={bulkMode}
          onClick={toggleBulkMode}
          className="h-6 px-2 text-xs font-normal text-primary"
        >
          Bulk edit
        </Button>
        {copyFrom?.value && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleCopyFrom}
            className="h-6 px-2 text-xs font-normal text-primary"
          >
            Same as {copyFrom.label}
          </Button>
        )}
      </div>

      {bulkMode && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/50 p-2.5 text-xs">
          <span className="text-muted-foreground">
            {selectedDays.size} day{selectedDays.size === 1 ? '' : 's'} selected
          </span>
          <input
            type="time"
            aria-label="Bulk open time"
            value={bulkOpen}
            onChange={(e) => setBulkOpen(e.target.value)}
            className={FIELD_CLASSES}
          />
          <span className="text-muted-foreground">–</span>
          <input
            type="time"
            aria-label="Bulk close time"
            value={bulkClose}
            onChange={(e) => setBulkClose(e.target.value)}
            className={FIELD_CLASSES}
          />
          <Button
            type="button"
            size="sm"
            disabled={selectedDays.size === 0}
            onClick={handleBulkApply}
            className="h-6 px-2 text-xs"
          >
            Apply
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={selectedDays.size === 0}
            onClick={handleBulkClose}
            className="h-6 px-2 text-xs font-normal text-muted-foreground"
          >
            Close these days
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setSelectedDays(new Set())}
            className="h-6 px-2 text-xs font-normal text-muted-foreground"
          >
            Clear selection
          </Button>
        </div>
      )}

      <div className="divide-y divide-border/60">
        {DAY_KEYS.map((day) => {
          const intervals = dayIntervals(day)
          const fullName = FULL_DAY_NAMES[day]
          const isOpen = intervals.length > 0
          const message = isOpen ? validateDay(intervals) : null
          return (
            <div key={day} className="flex items-start gap-2 py-1.5 text-sm">
              {bulkMode && (
                <input
                  type="checkbox"
                  aria-label={`Select ${fullName}`}
                  checked={selectedDays.has(day)}
                  onChange={(e) => toggleSelected(day, e.target.checked)}
                  className="mt-1.5"
                />
              )}
              <span
                className={cn(
                  'w-9 pt-1 text-xs font-medium uppercase tracking-wide',
                  isOpen ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {DAY_LABELS[day]}
              </span>
              <Switch
                size="sm"
                aria-label={`${fullName} open`}
                checked={isOpen}
                onCheckedChange={(checked) => handleToggleDay(day, checked)}
                className="mt-1"
              />
              <div className="flex-1 space-y-1">
                {!isOpen && (
                  <span className="text-xs text-muted-foreground italic">Closed</span>
                )}
                {intervals.map((interval, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <input
                      type="time"
                      aria-label={`${fullName} interval ${idx + 1} open time`}
                      value={interval[0]}
                      onChange={(e) => handleSetTime(day, idx, 0, e.target.value)}
                      className={FIELD_CLASSES}
                    />
                    <span className="text-muted-foreground">–</span>
                    <input
                      type="time"
                      aria-label={`${fullName} interval ${idx + 1} close time`}
                      value={interval[1]}
                      onChange={(e) => handleSetTime(day, idx, 1, e.target.value)}
                      className={FIELD_CLASSES}
                    />
                    <button
                      type="button"
                      aria-label={`Remove ${fullName} interval ${idx + 1}`}
                      onClick={() => handleRemoveInterval(day, idx)}
                      className="rounded-sm p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {message && <p className="text-xs text-destructive">{message}</p>}
              </div>
              <select
                aria-label={`Copy ${fullName} to`}
                value={copyTarget[day] ?? ''}
                onChange={(e) => {
                  const target = e.target.value
                  setCopyTarget((prev) => ({ ...prev, [day]: target }))
                }}
                className={cn(FIELD_CLASSES, 'px-1.5 text-xs text-muted-foreground')}
              >
                <option value="">Copy to…</option>
                <option value="all">All days</option>
                <option value="weekdays">Weekdays</option>
                <option value="weekend">Weekend</option>
              </select>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Copy ${fullName}`}
                disabled={!copyTarget[day]}
                onClick={() => handleCopy(day)}
                className="h-7 px-2 text-xs font-normal text-primary"
              >
                Copy
              </Button>
            </div>
          )
        })}
      </div>

      <p className="text-xs text-muted-foreground">{summarize(value)}</p>
    </div>
  )
}
